import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  Grid,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";

const DEFAULT_ARTWORK_SERVICE_TYPES = [
  "Vector Art",
  "Virtuals",
  "Creative Art",
  "Digitizing",
  "Image Editing",
  "Typeset",
];

const defaults = {
  proofProviderEnabled: false,
  proofProviderName: "binated",
  binatedApiKey: "",
  binatedApiSecret: "",
  binatedBaseUrl: "",
  binatedWebhookSecret: "",
  binatedHasApiKey: false,
  binatedHasApiSecret: false,
  binatedHasWebhookSecret: false,
  binatedApiKeyMasked: "",
  binatedApiSecretMasked: "",
  binatedWebhookSecretMasked: "",
  binatedArtworkServiceTypes: DEFAULT_ARTWORK_SERVICE_TYPES,
  binatedOtherServiceMode: "always",
  binatedOtherServiceServiceTypes: [],
  binatedProofEvaluationMode: "assess_both",
  binatedRequestProofEnabled: false,
};

export default function BinatedSettings({ instanceUniqueId }) {
  const SERVER = process.env.REACT_APP_NODEJS_SERVER_URL;
  const headers = { headers: { accessToken: localStorage.getItem("accessToken") } };

  const [form, setForm] = useState(defaults);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const load = async () => {
    if (!instanceUniqueId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${SERVER}/instances/${instanceUniqueId}/proof-provider-settings`, headers);
      const proofProvider = res.data?.proofProvider || {};
      const binated = proofProvider?.binated || {};
      setForm({
        ...defaults,
        proofProviderEnabled: proofProvider.enabled === true,
        proofProviderName: proofProvider.provider || "binated",
        binatedApiKey: binated.apiKey || "",
        binatedApiSecret: binated.apiSecret || "",
        binatedBaseUrl: binated.baseUrl || "",
        binatedWebhookSecret: binated.webhookSecret || "",
        binatedHasApiKey: binated.hasApiKey === true,
        binatedHasApiSecret: binated.hasApiSecret === true,
        binatedHasWebhookSecret: binated.hasWebhookSecret === true,
        binatedApiKeyMasked: binated.apiKeyMasked || "",
        binatedApiSecretMasked: binated.apiSecretMasked || "",
        binatedWebhookSecretMasked: binated.webhookSecretMasked || "",
        binatedArtworkServiceTypes:
          Array.isArray(binated.artworkServiceTypes) && binated.artworkServiceTypes.length
            ? binated.artworkServiceTypes
            : DEFAULT_ARTWORK_SERVICE_TYPES,
        binatedOtherServiceMode: binated?.otherServicePolicy?.mode || "always",
        binatedOtherServiceServiceTypes: Array.isArray(binated?.otherServicePolicy?.serviceTypes)
          ? binated.otherServicePolicy.serviceTypes
          : [],
        binatedProofEvaluationMode: binated?.otherServicePolicy?.proofEvaluationMode || "assess_both",
        binatedRequestProofEnabled: binated?.otherServicePolicy?.requestProofEnabled === true,
      });
    } catch (e) {
      alert(e?.response?.data?.message || "Failed to load Binated settings.");
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        proofProviderEnabled: form.proofProviderEnabled,
        proofProviderName: form.proofProviderName,
        proofProviderBinatedApiKey: form.binatedApiKey,
        proofProviderBinatedApiSecret: form.binatedApiSecret,
        proofProviderBinatedBaseUrl: form.binatedBaseUrl,
        proofProviderBinatedWebhookSecret: form.binatedWebhookSecret,
        proofProviderBinatedOtherServicePolicy: {
          mode: form.binatedOtherServiceMode,
          serviceTypes: form.binatedOtherServiceServiceTypes,
          proofEvaluationMode: form.binatedProofEvaluationMode,
          requestProofEnabled: form.binatedRequestProofEnabled,
        },
      };
      const res = await axios.put(
        `${SERVER}/instances/${instanceUniqueId}/proof-provider-settings`,
        payload,
        headers,
      );

      const proofProvider = res.data?.proofProvider || {};
      const binated = proofProvider?.binated || {};
      setForm({
        ...defaults,
        proofProviderEnabled: proofProvider.enabled === true,
        proofProviderName: proofProvider.provider || "binated",
        binatedApiKey: binated.apiKey || "",
        binatedApiSecret: binated.apiSecret || "",
        binatedBaseUrl: binated.baseUrl || "",
        binatedWebhookSecret: binated.webhookSecret || "",
        binatedHasApiKey: binated.hasApiKey === true,
        binatedHasApiSecret: binated.hasApiSecret === true,
        binatedHasWebhookSecret: binated.hasWebhookSecret === true,
        binatedApiKeyMasked: binated.apiKeyMasked || "",
        binatedApiSecretMasked: binated.apiSecretMasked || "",
        binatedWebhookSecretMasked: binated.webhookSecretMasked || "",
        binatedArtworkServiceTypes:
          Array.isArray(binated.artworkServiceTypes) && binated.artworkServiceTypes.length
            ? binated.artworkServiceTypes
            : DEFAULT_ARTWORK_SERVICE_TYPES,
        binatedOtherServiceMode: binated?.otherServicePolicy?.mode || "always",
        binatedOtherServiceServiceTypes: Array.isArray(binated?.otherServicePolicy?.serviceTypes)
          ? binated.otherServicePolicy.serviceTypes
          : [],
        binatedProofEvaluationMode: binated?.otherServicePolicy?.proofEvaluationMode || "assess_both",
        binatedRequestProofEnabled: binated?.otherServicePolicy?.requestProofEnabled === true,
      });
      alert("Proof provider settings saved.");
    } catch (e) {
      alert(e?.response?.data?.message || "Failed to save proof provider settings.");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceUniqueId]);

  return (
    <Card elevation={3} sx={{ borderRadius: 2 }}>
      <CardHeader
        title={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="h6">Proof Provider</Typography>
            {loading ? <CircularProgress size={16} /> : null}
          </Box>
        }
        action={
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button variant="outlined" onClick={load} disabled={loading || saving}>Refresh</Button>
            <Button variant="contained" onClick={save} disabled={loading || saving}>
              {saving ? "Saving..." : "Save"}
            </Button>
          </Box>
        }
        subheader="Enable and configure Binated proofing, artwork services, and purchasing behavior per instance."
      />
      <Divider />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.proofProviderEnabled}
                  onChange={(e) => setForm((f) => ({ ...f, proofProviderEnabled: e.target.checked }))}
                />
              }
              label="Enable third-party proof provider for this instance"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel id="proof-provider-select-label">Provider</InputLabel>
              <Select
                labelId="proof-provider-select-label"
                label="Provider"
                value={form.proofProviderName}
                onChange={(e) => setForm((f) => ({ ...f, proofProviderName: e.target.value }))}
              >
                <MenuItem value="binated">Binated</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="password"
              label="Binated API Key"
              value={form.binatedApiKey}
              onChange={setField("binatedApiKey")}
              placeholder={form.binatedHasApiKey ? form.binatedApiKeyMasked || "Configured" : "Enter API Key"}
              helperText={form.binatedHasApiKey ? `Saved: ${form.binatedApiKeyMasked}` : "Leave blank to keep empty."}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="password"
              label="Binated API Secret"
              value={form.binatedApiSecret}
              onChange={setField("binatedApiSecret")}
              placeholder={form.binatedHasApiSecret ? form.binatedApiSecretMasked || "Configured" : "Enter API Secret"}
              helperText={form.binatedHasApiSecret ? `Saved: ${form.binatedApiSecretMasked}` : "Leave blank to keep empty."}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Binated Base URL"
              value={form.binatedBaseUrl}
              onChange={setField("binatedBaseUrl")}
              placeholder="https://api.binated.com"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="password"
              label="Binated Webhook Secret"
              value={form.binatedWebhookSecret}
              onChange={setField("binatedWebhookSecret")}
              placeholder={form.binatedHasWebhookSecret ? form.binatedWebhookSecretMasked || "Configured" : "Enter Webhook Secret"}
              helperText={form.binatedHasWebhookSecret ? `Saved: ${form.binatedWebhookSecretMasked}` : "Used to verify incoming webhook events."}
            />
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
              Artwork Service purchasing
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Decide when a Binated request should generate an Order Cart Other Service. That Other Service is the billable source used to generate the designer Purchase Order.
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="binated-other-service-mode-label">Generate Other Service / PO</InputLabel>
              <Select
                labelId="binated-other-service-mode-label"
                label="Generate Other Service / PO"
                value={form.binatedOtherServiceMode}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    binatedOtherServiceMode: event.target.value,
                  }))
                }
              >
                <MenuItem value="always">Always create automatically</MenuItem>
                <MenuItem value="never">Do not create automatically</MenuItem>
                <MenuItem value="by_service_type">Choose by Artwork Service type</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {form.binatedOtherServiceMode === "by_service_type" ? (
            <>
              <Grid item xs={12}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Evaluation Mode
                </Typography>
                <RadioGroup
                  row
                  value={form.binatedProofEvaluationMode}
                  onChange={(event) => setForm((current) => ({
                    ...current,
                    binatedProofEvaluationMode: event.target.value,
                  }))}
                >
                  <FormControlLabel
                    value="proof_first"
                    control={<Radio size="small" />}
                    label="Request Proof First"
                  />
                  <FormControlLabel
                    value="assess_both"
                    control={<Radio size="small" />}
                    label="Assess Both Independently"
                  />
                </RadioGroup>
                <Typography variant="caption" color="text.secondary">
                  Proof First uses the Request Proof switch whenever proof is requested; otherwise it
                  checks artwork types. Assess Both allows either proof or artwork types to qualify.
                  Only one Other Service is generated per Binated request.
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Create an Other Service for:
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {(form.binatedArtworkServiceTypes || DEFAULT_ARTWORK_SERVICE_TYPES).map((serviceType) => {
                    const checked = form.binatedOtherServiceServiceTypes.includes(serviceType);
                    return (
                      <FormControlLabel
                        key={serviceType}
                        control={
                          <Switch
                            size="small"
                            checked={checked}
                            onChange={(event) =>
                              setForm((current) => ({
                                ...current,
                                binatedOtherServiceServiceTypes: event.target.checked
                                  ? Array.from(new Set([
                                      ...(current.binatedOtherServiceServiceTypes || []),
                                      serviceType,
                                    ]))
                                  : (current.binatedOtherServiceServiceTypes || []).filter(
                                      (entry) => entry !== serviceType
                                    ),
                              }))
                            }
                          />
                        }
                        label={serviceType}
                      />
                    );
                  })}
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={form.binatedRequestProofEnabled}
                        onChange={(event) => setForm((current) => ({
                          ...current,
                          binatedRequestProofEnabled: event.target.checked,
                        }))}
                      />
                    }
                    label="Request Proof"
                  />
                </Box>
              </Grid>
            </>
          ) : null}

        </Grid>
      </CardContent>
    </Card>
  );
}
